import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * 导出教研组手册为PDF
 * @param {HTMLElement} element - 要导出的元素
 * @param {string} fileName - 文件名
 * @param {Function} showMessage - 显示消息的函数，如 this.$message.error
 * @returns {Promise<void>}
 */
export async function exportHandbookToPDF(element, fileName, showMessage) {
  // 保存原始样式
  const originalStyles = {
    pages: [],
    assessmentTablePages: [],
    contentElements: []
  };
  
  // 保存并修改原始元素的样式（临时）
  const scrollableElements = element.querySelectorAll('.page');
  scrollableElements.forEach((el, index) => {
    originalStyles.pages[index] = {
      overflow: el.style.overflow,
      overflowY: el.style.overflowY,
      height: el.style.height,
      maxHeight: el.style.maxHeight
    };
    el.style.overflow = 'visible';
    el.style.overflowY = 'visible';
    el.style.height = 'auto';
    el.style.maxHeight = 'none';
  });
  
  // 移除assessment-table-page的滚动限制
  const assessmentTablePages = element.querySelectorAll('.assessment-table-page');
  assessmentTablePages.forEach((el, index) => {
    originalStyles.assessmentTablePages[index] = {
      overflow: el.style.overflow,
      overflowY: el.style.overflowY,
      height: el.style.height,
      maxHeight: el.style.maxHeight
    };
    el.style.overflow = 'visible';
    el.style.overflowY = 'visible';
    el.style.height = 'auto';
    el.style.maxHeight = 'none';
  });
  
  // 移除small-box中content的滚动限制，并确保宽度正确
  const contentElements = element.querySelectorAll('.small-box .content');
  contentElements.forEach((el, index) => {
    originalStyles.contentElements[index] = {
      overflow: el.style.overflow,
      overflowY: el.style.overflowY,
      height: el.style.height,
      maxHeight: el.style.maxHeight,
      width: el.style.width,
      maxWidth: el.style.maxWidth,
      minWidth: el.style.minWidth
    };
    el.style.overflow = 'visible';
    el.style.overflowY = 'visible';
    el.style.height = 'auto';
    el.style.maxHeight = 'none';
    // 强制设置宽度，确保占满父容器
    el.style.width = '100%';
    el.style.maxWidth = '100%';
    el.style.minWidth = '100%';
    el.style.boxSizing = 'border-box';
    
    // 同时确保 .word 元素也占满宽度
    const wordElements = el.querySelectorAll('.word');
    wordElements.forEach(wordEl => {
      wordEl.style.width = '100%';
      wordEl.style.maxWidth = '100%';
      wordEl.style.minWidth = '100%';
      wordEl.style.boxSizing = 'border-box';
      wordEl.style.display = 'block';
    });
  });
  
  // 恢复样式的函数
  const restoreStyles = () => {
    scrollableElements.forEach((el, idx) => {
      if (originalStyles.pages[idx]) {
        el.style.overflow = originalStyles.pages[idx].overflow;
        el.style.overflowY = originalStyles.pages[idx].overflowY;
        el.style.height = originalStyles.pages[idx].height;
        el.style.maxHeight = originalStyles.pages[idx].maxHeight;
      }
    });
    
    assessmentTablePages.forEach((el, idx) => {
      if (originalStyles.assessmentTablePages[idx]) {
        el.style.overflow = originalStyles.assessmentTablePages[idx].overflow;
        el.style.overflowY = originalStyles.assessmentTablePages[idx].overflowY;
        el.style.height = originalStyles.assessmentTablePages[idx].height;
        el.style.maxHeight = originalStyles.assessmentTablePages[idx].maxHeight;
      }
    });
    
    contentElements.forEach((el, idx) => {
      if (originalStyles.contentElements[idx]) {
        el.style.overflow = originalStyles.contentElements[idx].overflow;
        el.style.overflowY = originalStyles.contentElements[idx].overflowY;
        el.style.height = originalStyles.contentElements[idx].height;
        el.style.maxHeight = originalStyles.contentElements[idx].maxHeight;
        el.style.width = originalStyles.contentElements[idx].width || '';
        el.style.maxWidth = originalStyles.contentElements[idx].maxWidth || '';
        el.style.minWidth = originalStyles.contentElements[idx].minWidth || '';
      }
    });
  };
  
  // 等待DOM更新
  await new Promise(resolve => setTimeout(resolve, 300));
  
  try {
    const pageElements = element.querySelectorAll('.page');
    const PDF = new jsPDF("p", "pt", "a4");
    const pageWidth = 595.28; // A4宽度（pt）
    const pageHeight = 841.89; // A4高度（pt）
    let isFirstPage = true;
    
    // 递归处理每个页面元素
    const processPages = async (index) => {
      if (index >= pageElements.length) {
        restoreStyles();
        PDF.save(fileName + ".pdf");
        return;
      }
      
      const pageElement = pageElements[index];
      
      try {
        // 检查是否是评估表页面
        const isAssessmentTable = pageElement.classList.contains('assessment-table-page');
        
        // 先计算切分点
        const tables = pageElement.querySelectorAll('table');
        const breakPoints = [0]; // 起始点
        
        if (isAssessmentTable) {
          // 对于评估表，不使用按行切分，而是按照整个内容高度来处理
          // 让后续的分页逻辑按照页面高度自动切分
          breakPoints.push(pageElement.scrollHeight);
        } else {
          // 对于其他表格，为每个表格找到行的位置
          tables.forEach(table => {
            const rows = table.querySelectorAll('tr');
            rows.forEach((row) => {
              const rowTop = row.offsetTop;
              const rowHeight = row.offsetHeight;
              const rowBottom = rowTop + rowHeight;
              
              // 如果这一行不在已有的切分点附近，添加为切分点
              const isNearBreakPoint = breakPoints.some(bp => Math.abs(rowTop - bp) < 20);
              if (!isNearBreakPoint && rowTop > 0) {
                breakPoints.push(rowTop);
              }
              
              // 也添加行底部作为切分点
              if (rowBottom < pageElement.scrollHeight && !breakPoints.some(bp => Math.abs(rowBottom - bp) < 20)) {
                breakPoints.push(rowBottom);
              }
            });
          });
          
          // 如果没有找到合适的切分点，使用固定高度切分
          if (breakPoints.length < 2) {
            breakPoints.push(pageElement.scrollHeight);
          } else {
            breakPoints.push(pageElement.scrollHeight);
            breakPoints.sort((a, b) => a - b);
          }
        }
        
        // 如果是 small-box 页面，先处理样式和内容
        if (pageElement.classList.contains('small-box')) {
          const contentEl = pageElement.querySelector('.content');
          if (contentEl) {
            // 强制覆盖所有可能的宽度限制
            contentEl.style.width = '100%';
            contentEl.style.maxWidth = '100%';
            contentEl.style.minWidth = '100%';
            contentEl.style.flex = '1 1 0';
            contentEl.style.boxSizing = 'border-box';
            contentEl.style.overflow = 'visible';
            contentEl.style.overflowY = 'visible';
            contentEl.style.height = 'auto';
            contentEl.style.maxHeight = 'none';
            
            const wordEl = contentEl.querySelector('.word');
            if (wordEl) {
              // 强制设置 word 元素的宽度和显示方式
              wordEl.style.width = '100%';
              wordEl.style.maxWidth = '100%';
              wordEl.style.minWidth = '100%';
              wordEl.style.display = 'block';
              wordEl.style.boxSizing = 'border-box';
              wordEl.style.margin = '0';
              wordEl.style.padding = '0';
              wordEl.style.whiteSpace = 'normal';
              wordEl.style.wordBreak = 'break-all';
              
              // 确保所有子元素也占满宽度
              const allChildren = wordEl.querySelectorAll('*');
              allChildren.forEach(child => {
                child.style.maxWidth = '100%';
                child.style.boxSizing = 'border-box';
                child.style.wordBreak = 'break-all';
                child.style.whiteSpace = 'normal';
                if (child.tagName === 'STRONG') {
                  child.style.display = 'block';
                  child.style.width = '100%';
                  child.style.marginBottom = '8px';
                }
                if (child.tagName === 'BR') {
                  child.style.display = 'block';
                  child.style.content = '""';
                  child.style.margin = '0';
                }
              });
            }
          }
          
          // 确保 small-box 本身也占满宽度
          pageElement.style.width = '794px';
          pageElement.style.maxWidth = '794px';
          pageElement.style.minWidth = '794px';
          pageElement.style.height = 'auto';
          pageElement.style.maxHeight = 'none';
        }
        
        // 等待DOM更新，确保内容完全展开
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // 强制重新计算布局
        pageElement.offsetHeight;
        pageElement.scrollTop = 0; // 滚动到顶部确保内容可见
        
        // 检查页面类型
        const isSmallBox = pageElement.classList.contains('small-box');
        const isFacePage = pageElement.classList.contains('face');
        
        // 获取实际的内容高度和宽度（包括所有展开的内容）
        let actualHeight = Math.max(
          pageElement.scrollHeight,
          pageElement.offsetHeight,
          pageElement.clientHeight
        );
        let actualWidth = Math.max(
          pageElement.scrollWidth,
          pageElement.offsetWidth,
          pageElement.clientWidth
        );
        
        // 对于封面页，固定尺寸
        if (isFacePage) {
          actualWidth = 794;
          actualHeight = 1123;
          console.log('Face page dimensions set:', { actualWidth, actualHeight });
        } else if (isSmallBox) {
          // 对于 small-box 页面，确保获取完整的内容高度
          const contentEl = pageElement.querySelector('.content');
          const wordEl = contentEl ? contentEl.querySelector('.word') : null;
          if (wordEl) {
            // 强制重新计算布局
            wordEl.offsetHeight;
            contentEl.offsetHeight;
            
            // 获取 word 元素的实际高度（使用所有可能的高度值）
            const wordScrollHeight = wordEl.scrollHeight;
            const wordOffsetHeight = wordEl.offsetHeight;
            const wordClientHeight = wordEl.clientHeight;
            const wordHeight = Math.max(wordScrollHeight, wordOffsetHeight, wordClientHeight);
            
            // 获取 content 元素的高度
            const contentScrollHeight = contentEl.scrollHeight;
            const contentOffsetHeight = contentEl.offsetHeight;
            const contentClientHeight = contentEl.clientHeight;
            const contentHeight = Math.max(contentScrollHeight, contentOffsetHeight, contentClientHeight);
            
            // 获取页面标题的高度
            const titleEl = pageElement.querySelector('.page-title.title');
            const titleHeight = titleEl ? Math.max(titleEl.scrollHeight, titleEl.offsetHeight, 30) : 30;
            
            // 计算总高度：标题 + 内容 + 页面padding
            const pagePadding = 80; // @tableGap * 2
            const totalHeight = titleHeight + contentHeight + pagePadding + 20; // 20是margin-top
            
            // 使用最大的高度值
            actualHeight = Math.max(actualHeight, totalHeight, wordHeight + 200);
            
            // 确保高度至少是内容高度的1.2倍，以防遗漏
            if (wordHeight > 0) {
              actualHeight = Math.max(actualHeight, wordHeight * 1.2 + 200);
            }
          }
          // 确保宽度是完整的页面宽度
          actualWidth = 794; // 固定为页面宽度
        }
        const canvasOptions = {
          allowTaint: true,
          useCORS: true,
          scale: 2,
          logging: false,
          backgroundColor: '#ffffff',
          width: actualWidth,
          height: actualHeight,
          windowWidth: actualWidth,
          windowHeight: actualHeight,
          scrollX: 0,
          scrollY: 0
        };
        
        // 设置 onclone 回调
        if (isSmallBox || isFacePage) {
          canvasOptions.removeContainer = false;
          canvasOptions.onclone = (clonedDoc) => {
            // 处理封面页格式 - 调整元素位置并重建info部分
            if (isFacePage) {
              const clonedFacePage = clonedDoc.querySelector('.page.face');
              if (clonedFacePage) {
                const titleEl = clonedFacePage.querySelector('.title');
                const infoEl = clonedFacePage.querySelector('.info');
                const schoolYearEl = clonedFacePage.querySelector('.schoolYear');
                
                if (titleEl && infoEl && schoolYearEl) {
                  // 获取数据（在移除元素之前）
                  const schoolYearText = schoolYearEl.textContent.trim();
                  const titleH2s = titleEl.querySelectorAll('h2');
                  const infoH2s = infoEl.querySelectorAll('h2');
                  
                  // 完全移除原来的 infoEl，避免重叠和干扰
                  if (infoEl.parentNode) {
                    infoEl.parentNode.removeChild(infoEl);
                  }
                  
                  // 重新设置页面样式
                  clonedFacePage.style.display = 'block';
                  clonedFacePage.style.position = 'relative';
                  clonedFacePage.style.width = '794px';
                  clonedFacePage.style.height = '1123px';
                  clonedFacePage.style.backgroundColor = '#ffffff';
                  clonedFacePage.style.padding = '0';
                  clonedFacePage.style.margin = '0';
                  clonedFacePage.style.overflow = 'visible';
                  
                  // 调整标题位置 - 移到顶部
                  titleEl.style.position = 'absolute';
                  titleEl.style.top = '150px';
                  titleEl.style.left = '50%';
                  titleEl.style.transform = 'translateX(-50%)';
                  titleEl.style.fontSize = '32px';
                  titleEl.style.textAlign = 'center';
                  titleEl.style.zIndex = '100';
                  titleEl.style.color = '#000000';
                  titleEl.style.width = '100%';
                  titleEl.style.margin = '0';
                  titleEl.style.padding = '0';
                  titleEl.style.boxSizing = 'border-box';
                  titleEl.style.backgroundColor = 'transparent';
                  titleEl.style.background = 'none';
                  
                  // 确保标题中的h2元素也正确设置
                  titleH2s.forEach((h2, index) => {
                    const originalText = h2.textContent.trim();
                    console.log(`Title H2 ${index} original:`, originalText);
                    
                    h2.style.color = '#000000';
                    h2.style.fontWeight = 'bold';
                    h2.style.margin = '15px 0';
                    h2.style.lineHeight = '1.5';
                    h2.style.display = 'block';
                    h2.style.width = '100%';
                    h2.style.textAlign = 'center';
                    h2.style.boxSizing = 'border-box';
                    h2.style.padding = '0';
                    
                    // 确保文本内容正确（去除可能的重复）
                    const cleanText = originalText.replace(/上海市奉贤区上海市奉贤区/g, '上海市奉贤区');
                    if (cleanText !== originalText) {
                      h2.textContent = cleanText;
                      console.log(`Title H2 ${index} cleaned:`, cleanText);
                    }
                  });
                  
                  // 重建info部分 - 使用带下划线的格式，位置在标题下方
                  const newInfoContainer = clonedDoc.createElement('div');
                  newInfoContainer.style.position = 'absolute';
                  newInfoContainer.style.top = '400px';
                  newInfoContainer.style.left = '50%';
                  newInfoContainer.style.transform = 'translateX(-50%)';
                  newInfoContainer.style.width = '60%';
                  newInfoContainer.style.zIndex = '100';
                  newInfoContainer.style.backgroundColor = 'transparent';
                  newInfoContainer.style.background = 'none';
                  newInfoContainer.style.overflow = 'visible';
                  
                  // 组别
                  if (infoH2s.length >= 1) {
                    const groupSpan = infoH2s[0].querySelector('span');
                    const groupValue = groupSpan ? groupSpan.textContent.trim() : '';
                    
                    console.log('Group value:', groupValue);
                    
                    const groupItem = clonedDoc.createElement('div');
                    groupItem.style.display = 'flex';
                    groupItem.style.alignItems = 'flex-end';
                    groupItem.style.margin = '20px 0';
                    groupItem.style.fontSize = '18px';
                    groupItem.style.color = '#000000';
                    groupItem.style.backgroundColor = 'transparent';
                    groupItem.style.position = 'relative';
                    
                    const groupLabel = clonedDoc.createElement('span');
                    groupLabel.textContent = '组别：';
                    groupLabel.style.minWidth = '60px';
                    groupLabel.style.textAlign = 'right';
                    groupLabel.style.marginRight = '10px';
                    groupLabel.style.color = '#000000';
                    groupLabel.style.flexShrink = '0';
                    
                    const groupUnderlineContainer = clonedDoc.createElement('div');
                    groupUnderlineContainer.style.flex = '1';
                    groupUnderlineContainer.style.position = 'relative';
                    groupUnderlineContainer.style.display = 'flex';
                    groupUnderlineContainer.style.flexDirection = 'column';
                    groupUnderlineContainer.style.alignItems = 'center';
                    
                    const groupValueSpan = clonedDoc.createElement('span');
                    groupValueSpan.textContent = groupValue;
                    groupValueSpan.style.fontWeight = '700';
                    groupValueSpan.style.color = '#000000';
                    groupValueSpan.style.marginBottom = '5px';
                    groupValueSpan.style.display = 'block';
                    
                    const groupUnderline = clonedDoc.createElement('div');
                    groupUnderline.style.width = '100%';
                    groupUnderline.style.borderBottom = '1px solid #000';
                    groupUnderline.style.height = '1px';
                    groupUnderline.style.backgroundColor = 'transparent';
                    
                    groupUnderlineContainer.appendChild(groupValueSpan);
                    groupUnderlineContainer.appendChild(groupUnderline);
                    
                    groupItem.appendChild(groupLabel);
                    groupItem.appendChild(groupUnderlineContainer);
                    newInfoContainer.appendChild(groupItem);
                  }
                  
                  // 组长
                  if (infoH2s.length >= 2) {
                    const leaderSpan = infoH2s[1].querySelector('span');
                    const leaderValue = leaderSpan ? leaderSpan.textContent.trim() : '';
                    
                    console.log('Leader value:', leaderValue);
                    
                    const leaderItem = clonedDoc.createElement('div');
                    leaderItem.style.display = 'flex';
                    leaderItem.style.alignItems = 'flex-end';
                    leaderItem.style.margin = '20px 0';
                    leaderItem.style.fontSize = '18px';
                    leaderItem.style.color = '#000000';
                    leaderItem.style.backgroundColor = 'transparent';
                    leaderItem.style.position = 'relative';
                    
                    const leaderLabel = clonedDoc.createElement('span');
                    leaderLabel.textContent = '组长：';
                    leaderLabel.style.minWidth = '60px';
                    leaderLabel.style.textAlign = 'right';
                    leaderLabel.style.marginRight = '10px';
                    leaderLabel.style.color = '#000000';
                    leaderLabel.style.flexShrink = '0';
                    
                    const leaderUnderlineContainer = clonedDoc.createElement('div');
                    leaderUnderlineContainer.style.flex = '1';
                    leaderUnderlineContainer.style.position = 'relative';
                    leaderUnderlineContainer.style.display = 'flex';
                    leaderUnderlineContainer.style.flexDirection = 'column';
                    leaderUnderlineContainer.style.alignItems = 'center';
                    
                    const leaderValueSpan = clonedDoc.createElement('span');
                    leaderValueSpan.textContent = leaderValue;
                    leaderValueSpan.style.fontWeight = '700';
                    leaderValueSpan.style.color = '#000000';
                    leaderValueSpan.style.marginBottom = '5px';
                    leaderValueSpan.style.display = 'block';
                    
                    const leaderUnderline = clonedDoc.createElement('div');
                    leaderUnderline.style.width = '100%';
                    leaderUnderline.style.borderBottom = '1px solid #000';
                    leaderUnderline.style.height = '1px';
                    leaderUnderline.style.backgroundColor = 'transparent';
                    
                    leaderUnderlineContainer.appendChild(leaderValueSpan);
                    leaderUnderlineContainer.appendChild(leaderUnderline);
                    
                    leaderItem.appendChild(leaderLabel);
                    leaderItem.appendChild(leaderUnderlineContainer);
                    newInfoContainer.appendChild(leaderItem);
                  }
                  
                  // 确保 newInfoContainer 有内容才添加
                  if (newInfoContainer.children.length > 0) {
                    clonedFacePage.appendChild(newInfoContainer);
                    console.log('New info container added with', newInfoContainer.children.length, 'items');
                  } else {
                    console.warn('New info container is empty, not adding');
                  }
                  
                  // 调整底部学年学期位置
                  schoolYearEl.style.position = 'absolute';
                  schoolYearEl.style.bottom = '150px';
                  schoolYearEl.style.left = '50%';
                  schoolYearEl.style.transform = 'translateX(-50%)';
                  schoolYearEl.style.fontSize = '20px';
                  schoolYearEl.style.fontWeight = '700';
                  schoolYearEl.style.textAlign = 'center';
                  schoolYearEl.style.zIndex = '100';
                  schoolYearEl.style.color = '#000000';
                  schoolYearEl.style.width = 'auto';
                  schoolYearEl.style.margin = '0';
                  schoolYearEl.style.padding = '0';
                  schoolYearEl.style.backgroundColor = 'transparent';
                  schoolYearEl.style.background = 'none';
                  
                  // 确保学年学期中的h2元素也正确设置
                  const schoolYearH2 = schoolYearEl.querySelector('h2');
                  if (schoolYearH2) {
                    schoolYearH2.style.color = '#000000';
                    schoolYearH2.style.fontWeight = '700';
                    schoolYearH2.style.margin = '0';
                    schoolYearH2.style.padding = '0';
                    schoolYearH2.style.display = 'block';
                  }
                  
                  console.log('Face page elements added:', {
                    newInfoContainer: !!newInfoContainer,
                    newInfoContainerChildren: newInfoContainer ? newInfoContainer.children.length : 0,
                    titleEl: !!titleEl,
                    schoolYearEl: !!schoolYearEl,
                    schoolYearText: schoolYearText,
                    clonedFacePageChildren: clonedFacePage.children.length,
                    allChildren: Array.from(clonedFacePage.children).map(c => ({
                      tag: c.tagName,
                      class: c.className,
                      text: c.textContent ? c.textContent.substring(0, 50) : ''
                    }))
                  });
                } else {
                  console.warn('Face page elements not found:', {
                    titleEl: !!titleEl,
                    infoEl: !!infoEl,
                    schoolYearEl: !!schoolYearEl
                  });
                }
              } else {
                console.warn('Cloned face page not found');
              }
            }
            
            // 处理 small-box 页面
            if (isSmallBox) {
              const clonedPage = clonedDoc.querySelector('.page.small-box');
              if (clonedPage) {
                clonedPage.style.width = '794px';
                clonedPage.style.maxWidth = '794px';
                clonedPage.style.minWidth = '794px';
                clonedPage.style.height = 'auto';
                clonedPage.style.maxHeight = 'none';
                clonedPage.style.overflow = 'visible';
                clonedPage.style.overflowY = 'visible';
                
                const clonedContent = clonedPage.querySelector('.content');
                if (clonedContent) {
                  clonedContent.style.width = '100%';
                  clonedContent.style.maxWidth = '100%';
                  clonedContent.style.minWidth = '100%';
                  clonedContent.style.height = 'auto';
                  clonedContent.style.maxHeight = 'none';
                  clonedContent.style.overflow = 'visible';
                  clonedContent.style.overflowY = 'visible';
                  
                  const clonedWord = clonedContent.querySelector('.word');
                  if (clonedWord) {
                    clonedWord.style.width = '100%';
                    clonedWord.style.maxWidth = '100%';
                    clonedWord.style.minWidth = '100%';
                    clonedWord.style.display = 'block';
                    clonedWord.style.height = 'auto';
                    clonedWord.style.maxHeight = 'none';
                    clonedWord.style.overflow = 'visible';
                    clonedWord.style.overflowY = 'visible';
                    
                    // 确保所有子元素也正确设置
                    const allChildren = clonedWord.querySelectorAll('*');
                    allChildren.forEach(child => {
                      child.style.maxWidth = '100%';
                      child.style.boxSizing = 'border-box';
                      if (child.tagName === 'STRONG') {
                        child.style.display = 'block';
                        child.style.width = '100%';
                      }
                    });
                  }
                }
              }
            }
          };
        }
        
        // 生成当前页面的canvas
        let canvas = await html2canvas(pageElement, canvasOptions);
        
        let imgWidth = canvas.width;
        let imgHeight = canvas.height;
        
        // 对于封面页，检查并修复高度
        if (isFacePage) {
          const expectedHeight = actualHeight * 2; // scale 是 2
          console.log(`Face page: actualHeight=${actualHeight}, expectedCanvasHeight=${expectedHeight}, actualCanvasHeight=${imgHeight}`);
          
          // 如果实际高度小于期望高度，说明高度有问题
          if (imgHeight < expectedHeight * 0.5) {
            console.warn(`Canvas height ${imgHeight} is less than expected ${expectedHeight}, attempting to fix...`);
            
            // 重新生成，使用更明确的配置
            const fixedOptions = {
              ...canvasOptions,
              width: 794,
              height: 1123,
              windowWidth: 794,
              windowHeight: 1123,
              scale: 2,
              onclone: (clonedDoc) => {
                // 合并原有的 onclone 逻辑
                if (canvasOptions.onclone) {
                  canvasOptions.onclone(clonedDoc);
                }
                
                // 确保封面页有正确的高度
                const clonedFacePage = clonedDoc.querySelector('.page.face');
                if (clonedFacePage) {
                  clonedFacePage.style.width = '794px';
                  clonedFacePage.style.height = '1123px';
                  clonedFacePage.style.minHeight = '1123px';
                  clonedFacePage.style.maxHeight = '1123px';
                  clonedFacePage.style.overflow = 'visible';
                }
              }
            };
            
            canvas = await html2canvas(pageElement, fixedOptions);
            imgWidth = canvas.width;
            imgHeight = canvas.height;
            console.log(`Regenerated face page canvas: width=${imgWidth}, height=${imgHeight}`);
          }
        }
        
        // 对于 small-box 页面，检查并修复宽度
        if (isSmallBox) {
          // 期望的宽度（794px * scale 2 = 1588px）
          const expectedWidth = actualWidth * 2; // scale 是 2
          
          console.log(`Small-box page: actualWidth=${actualWidth}, expectedCanvasWidth=${expectedWidth}, actualCanvasWidth=${imgWidth}`);
          
          // 如果实际宽度小于期望宽度，说明宽度有问题
          if (imgWidth < expectedWidth * 0.9) {
            console.warn(`Canvas width ${imgWidth} is less than expected ${expectedWidth}, attempting to fix...`);
            
            // 重新生成，使用更明确的配置
            const fixedOptions = {
              ...canvasOptions,
              width: actualWidth,
              height: actualHeight,
              windowWidth: actualWidth,
              windowHeight: actualHeight,
              scale: 2,
              onclone: (clonedDoc) => {
                // 确保 export-box 也有正确的宽度
                const clonedExportBox = clonedDoc.querySelector('.export-box');
                if (clonedExportBox) {
                  clonedExportBox.style.width = '794px';
                  clonedExportBox.style.maxWidth = '794px';
                }
                
                const clonedPage = clonedDoc.querySelector('.page.small-box');
                if (clonedPage) {
                  clonedPage.style.width = '794px';
                  clonedPage.style.maxWidth = '794px';
                  clonedPage.style.minWidth = '794px';
                  clonedPage.style.height = 'auto';
                  clonedPage.style.maxHeight = 'none';
                  clonedPage.style.overflow = 'visible';
                  clonedPage.style.overflowY = 'visible';
                  clonedPage.style.position = 'relative';
                  
                  const clonedContent = clonedPage.querySelector('.content');
                  if (clonedContent) {
                    clonedContent.style.width = '100%';
                    clonedContent.style.maxWidth = '100%';
                    clonedContent.style.minWidth = '100%';
                    clonedContent.style.height = 'auto';
                    clonedContent.style.maxHeight = 'none';
                    clonedContent.style.overflow = 'visible';
                    clonedContent.style.overflowY = 'visible';
                    clonedContent.style.flex = '1 1 0';
                    clonedContent.style.boxSizing = 'border-box';
                    
                    const clonedWord = clonedContent.querySelector('.word');
                    if (clonedWord) {
                      clonedWord.style.width = '100%';
                      clonedWord.style.maxWidth = '100%';
                      clonedWord.style.minWidth = '100%';
                      clonedWord.style.display = 'block';
                      clonedWord.style.height = 'auto';
                      clonedWord.style.maxHeight = 'none';
                      clonedWord.style.overflow = 'visible';
                      clonedWord.style.overflowY = 'visible';
                      clonedWord.style.boxSizing = 'border-box';
                      clonedWord.style.margin = '0';
                      clonedWord.style.padding = '0';
                      
                      // 确保所有子元素也正确设置
                      const allChildren = clonedWord.querySelectorAll('*');
                      allChildren.forEach(child => {
                        child.style.maxWidth = '100%';
                        child.style.boxSizing = 'border-box';
                        child.style.wordBreak = 'break-all';
                        child.style.whiteSpace = 'normal';
                        if (child.tagName === 'STRONG') {
                          child.style.display = 'block';
                          child.style.width = '100%';
                          child.style.marginBottom = '8px';
                        }
                        if (child.tagName === 'BR') {
                          child.style.display = 'block';
                        }
                      });
                    }
                  }
                }
              }
            };
            
            const fixedCanvas = await html2canvas(pageElement, fixedOptions);
            const fixedWidth = fixedCanvas.width;
            const fixedHeight = fixedCanvas.height;
            
            // 如果修复后的宽度更接近期望值，使用修复后的 canvas
            if (fixedWidth >= imgWidth && fixedWidth >= expectedWidth * 0.8) {
              canvas = fixedCanvas;
              imgWidth = fixedWidth;
              imgHeight = fixedHeight;
            } else if (imgWidth < expectedWidth * 0.8) {
              // 如果还是不对，尝试使用实际元素宽度重新生成
              console.warn(`Canvas width still incorrect: ${fixedWidth}, using element dimensions`);
              const computedWidth = Math.max(
                pageElement.offsetWidth || 794,
                pageElement.clientWidth || 794,
                pageElement.scrollWidth || 794
              );
              const computedHeight = Math.max(
                pageElement.scrollHeight || actualHeight,
                pageElement.offsetHeight || actualHeight,
                pageElement.clientHeight || actualHeight
              );
              
              // 使用计算出的尺寸重新生成
              const finalOptions = {
                ...fixedOptions,
                width: computedWidth,
                height: computedHeight,
                windowWidth: computedWidth,
                windowHeight: computedHeight
              };
              
              canvas = await html2canvas(pageElement, finalOptions);
              imgWidth = canvas.width;
              imgHeight = canvas.height;
            }
          }
        }
        
        // 对于 small-box 页面，检查 canvas 内容是否占满宽度
        if (isSmallBox) {
          const expectedWidthForCheck = actualWidth * 2; // scale 是 2
          
          // 检查 canvas 右侧是否有内容（通过采样右侧像素）
          const sampleX = Math.floor(imgWidth * 0.9); // 90% 位置
          const sampleY = Math.floor(imgHeight / 2); // 中间位置
          
          try {
            const imageData = canvas.getContext('2d').getImageData(sampleX, sampleY, 10, 10);
            const pixels = imageData.data;
            
            // 检查是否有非白色像素（说明有内容）
            let hasContent = false;
            for (let i = 0; i < pixels.length; i += 4) {
              const r = pixels[i];
              const g = pixels[i + 1];
              const b = pixels[i + 2];
              // 如果不是白色（允许一些误差）
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            
            console.log(`Canvas content check: sampleX=${sampleX}, hasContent=${hasContent}`);
            
            // 如果右侧没有内容，说明内容只占了一半宽度，跳过检测，直接使用原始 canvas
            // 注意：这个问题可能是 html2canvas 的渲染问题，暂时不处理
            if (!hasContent && imgWidth >= expectedWidthForCheck * 0.9) {
              console.warn('Canvas width is correct but content only occupies left half. This may be a html2canvas rendering issue.');
            }
          } catch (e) {
            console.warn('Failed to check canvas content:', e);
          }
        }
        
        const imgData = canvas.toDataURL("image/jpeg", 1.0);
        
        // 计算缩放比例以适应A4页面宽度
        const scaleX = pageWidth / imgWidth;
        const scale = scaleX; // 只按宽度缩放，保持比例
        
        const scaledWidth = imgWidth * scale;
        const scaledHeight = imgHeight * scale;
        
        console.log(`PDF scaling: imgWidth=${imgWidth}, pageWidth=${pageWidth}, scale=${scale}, scaledWidth=${scaledWidth}`);
        
        // 如果缩放后的高度超过一页，需要智能分页
        if (scaledHeight > pageHeight) {
          if (isAssessmentTable || isSmallBox) {
            // 对于评估表和 small-box 页面，按照页面高度来切分
            const pageHeightInCanvas = pageHeight / scale; // 转换为canvas坐标的页面高度
            let currentY = 0;
            
            while (currentY < imgHeight) {
              const segmentHeight = Math.min(pageHeightInCanvas, imgHeight - currentY);
              const scaledSegmentHeight = segmentHeight * scale;
              
              if (!isFirstPage || currentY > 0) {
                PDF.addPage();
              }
              isFirstPage = false;
              
              // 创建临时canvas来裁剪图片
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = imgWidth;
              tempCanvas.height = segmentHeight;
              const tempCtx = tempCanvas.getContext('2d');
              tempCtx.drawImage(canvas, 0, currentY, imgWidth, segmentHeight, 0, 0, imgWidth, segmentHeight);
              
              const tempImgData = tempCanvas.toDataURL("image/jpeg", 1.0);
              PDF.addImage(tempImgData, "JPEG", 0, 0, scaledWidth, scaledSegmentHeight);
              
              currentY += pageHeightInCanvas;
            }
          } else {
            // 将切分点转换为canvas坐标
            const canvasBreakPoints = breakPoints.map(bp => (bp / pageElement.scrollHeight) * imgHeight);
            
            // 按切分点分页
            for (let i = 0; i < canvasBreakPoints.length - 1; i++) {
              const startY = canvasBreakPoints[i];
              const endY = canvasBreakPoints[i + 1];
              const segmentHeight = endY - startY;
              const scaledSegmentHeight = segmentHeight * scale;
              
              // 如果这一段超过一页，需要进一步切分
              if (scaledSegmentHeight > pageHeight) {
                // 如果单个段落就超过一页，按比例缩小
                const segmentScale = pageHeight / scaledSegmentHeight;
                const finalWidth = scaledWidth * segmentScale;
                const finalHeight = scaledSegmentHeight * segmentScale;
                
                if (!isFirstPage || i > 0) {
                  PDF.addPage();
                }
                isFirstPage = false;
                
                // 创建临时canvas来裁剪图片
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = imgWidth;
                tempCanvas.height = segmentHeight;
                const tempCtx = tempCanvas.getContext('2d');
                tempCtx.drawImage(canvas, 0, startY, imgWidth, segmentHeight, 0, 0, imgWidth, segmentHeight);
                
                const tempImgData = tempCanvas.toDataURL("image/jpeg", 1.0);
                PDF.addImage(tempImgData, "JPEG", 0, 0, finalWidth, finalHeight);
              } else {
                // 这一段可以放在一页内
                if (!isFirstPage || i > 0) {
                  PDF.addPage();
                }
                isFirstPage = false;
                
                // 创建临时canvas来裁剪图片
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = imgWidth;
                tempCanvas.height = segmentHeight;
                const tempCtx = tempCanvas.getContext('2d');
                tempCtx.drawImage(canvas, 0, startY, imgWidth, segmentHeight, 0, 0, imgWidth, segmentHeight);
                
                const tempImgData = tempCanvas.toDataURL("image/jpeg", 1.0);
                PDF.addImage(tempImgData, "JPEG", 0, 0, scaledWidth, scaledSegmentHeight);
              }
            }
          }
        } else {
          // 内容可以放在一页内
          if (!isFirstPage) {
            PDF.addPage();
          }
          isFirstPage = false;
          
          // 检查当前页面类型（需要重新检查，因为可能在重新生成 canvas 后变量作用域有问题）
          const currentIsSmallBox = pageElement.classList.contains('small-box');
          const currentIsFacePage = pageElement.classList.contains('face');
          
          // 对于 small-box 和 face 页面，从左上角开始，不居中
          // 对于其他页面，居中显示
          let x = 0;
          let y = 0;
          
          if (!currentIsSmallBox && !currentIsFacePage) {
          // 居中显示
            x = (pageWidth - scaledWidth) / 2;
            y = (pageHeight - scaledHeight) / 2;
          }
          
          console.log(`Adding image to PDF: x=${x}, y=${y}, width=${scaledWidth}, height=${scaledHeight}, isSmallBox=${currentIsSmallBox}, isFacePage=${currentIsFacePage}`);
          PDF.addImage(imgData, "JPEG", x, y, scaledWidth, scaledHeight);
        }
        
        // 处理下一页
        await processPages(index + 1);
      } catch (error) {
        console.error(`处理第${index + 1}页失败:`, error);
        // 即使出错也继续处理下一页
        await processPages(index + 1);
      }
    };
    
    // 开始处理第一页
    await processPages(0);
  } catch (error) {
    console.error('导出失败:', error);
    if (showMessage) {
      showMessage('导出失败，请重试');
    }
    restoreStyles();
    throw error;
  }
}
